import React, { Dispatch, SetStateAction, useEffect } from 'react';
import ActionButton from '@westwing/ui-kit/ActionButton';
import useForm from '@westwing/ui-kit/Tools/hooks/useForm';
import FloatingLabelInput, { Hint } from '@westwing/ui-kit/FloatingLabelInput';
import styled from 'styled-components';
import NotificationBanner from '@westwing/ui-kit/NotificationBanner';
import {
    StyledForm,
    StyledFieldset,
    StyledParagraphCentered,
    StyledLink,
    StyledFormActions,
} from '../loginAndRegistration.style';
import { VALIDATE_HOOK } from '../loginAndRegistration.types';
import testIds from '../testId.e2e';
import BellaForgotPassword from '../BellaForgotPassword';
import loginSchema from './loginForm.schema';
import useTranslate from 'Hooks/useTranslate';

const StyledLinkWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
`;

type LoginFormValues = {
    password: string;
    email: string;
};

export type LoginFormProps = {
    isActive?: boolean;
    isLoading?: boolean;
    knownEmail?: string;
    forgotPasswordLink: string;
    onSubmit: (values: LoginFormValues) => void;
    emailDisabled?: boolean;
    description?: string;
    hasApiError?: boolean;
    setHasApiError: React.Dispatch<React.SetStateAction<boolean>>;
    onForgotPasswordClick?: () => void;
    isBellaForgotPasswordEnabled?: boolean;
};

const LoginForm = ({
    isActive = true,
    isLoading = false,
    knownEmail,
    emailDisabled = false,
    hasApiError = false,
    description: descriptionText,
    forgotPasswordLink,
    onSubmit: onFormSubmit,
    setHasApiError,
    onForgotPasswordClick,
    isBellaForgotPasswordEnabled,
}: LoginFormProps): JSX.Element => {
    const { t, tPlain } = useTranslate();

    const description = descriptionText || tPlain('checkout_login_motivation');
    const passwordLabel = tPlain('Password');
    const emailLabel = tPlain('E-Mail');
    const submitButtonLabel = t('Login');
    const forgotPasswordLabel = tPlain('Forgot password_bella');
    const notificationBannerLabel = tPlain('Incorrect email or password_bella');

    const schema = loginSchema(tPlain);
    const { values, touched, errors, onBlur, onSubmit, onChange, setValues, isValidated, isValid } =
        useForm<LoginFormValues>({
            schema,
            onSubmitValid: onFormSubmit,
            validateOn: [VALIDATE_HOOK.BLUR, VALIDATE_HOOK.SUBMIT],
            initialValues: {
                email: knownEmail || '',
                password: '',
            },
        });
    useEffect(() => {
        (setValues as Dispatch<SetStateAction<LoginFormValues>>)((previousValues: LoginFormValues) => ({
            ...previousValues,
            email: knownEmail || '',
        }));
    }, [knownEmail, setValues]);

    const isEmailValid = () => {
        if (errors?.email) {
            return false;
        }
        if (touched.email || isValidated('email')) {
            return true;
        }
        return undefined;
    };
    const showHint = (field: keyof LoginFormValues) => (touched[field] || isValidated(field)) && errors[field];

    return (
        <div>
            <StyledForm onSubmit={onSubmit} noValidate>
                <StyledFieldset disabled={!isActive}>
                    <StyledParagraphCentered>{description}</StyledParagraphCentered>
                    {hasApiError && (
                        <NotificationBanner
                            bannerType="card"
                            displayType="error"
                            notificationDataTestId={testIds.loginAPIError}
                            message={notificationBannerLabel}
                            messageId="login_error"
                            dismissable
                            isSticky
                            onMessageDismissed={() => setHasApiError(false)}
                        />
                    )}
                    <FloatingLabelInput
                        id="login_email"
                        label={emailLabel}
                        value={values.email}
                        type="email"
                        name="email"
                        onChange={onChange}
                        onBlur={onBlur}
                        autoComplete="email"
                        isValid={isEmailValid()}
                        hint={showHint('email') && <Hint.Error as="output">{errors.email[0]}</Hint.Error>}
                        disabled={emailDisabled}
                    />
                    <div>
                        <FloatingLabelInput
                            id="login_password"
                            label={passwordLabel}
                            type="password"
                            name="password"
                            autoComplete="current-password"
                            value={values.password}
                            onChange={onChange}
                            onBlur={onBlur}
                            isValid={isValid('password')}
                            hint={showHint('password') && <Hint.Error as="output">{errors.password[0]}</Hint.Error>}
                        />
                        {isBellaForgotPasswordEnabled ? (
                            <BellaForgotPassword forgotPasswordLabel={forgotPasswordLabel} email={values.email} />
                        ) : (
                            <StyledLinkWrapper>
                                {isActive && (
                                    <StyledLink to={forgotPasswordLink} onClick={onForgotPasswordClick}>
                                        {forgotPasswordLabel}
                                    </StyledLink>
                                )}
                                {!isActive && <span>{forgotPasswordLabel}</span>}
                            </StyledLinkWrapper>
                        )}
                    </div>

                    <StyledFormActions>
                        <ActionButton type="submit" isLoading={isLoading}>
                            {submitButtonLabel}
                        </ActionButton>
                    </StyledFormActions>
                </StyledFieldset>
            </StyledForm>
        </div>
    );
};
export default LoginForm;
